import api from './api';
import { API_ROUTES } from '@/lib/constants';
import { createCrudService, createSubResourceGetter } from './crud.factory';
import type { Deal, ApiResponse, Activity, Note, Task } from '@/types/table';

interface PipelineStage {
  id: string;
  name: string;
  position: number;
  probability: number;
  color?: string;
  pipelineId: string;
  deals: Deal[];
}

export const dealService = {
  ...createCrudService<Deal>(API_ROUTES.DEALS),

  getPipelineDeals: async (pipelineId: string): Promise<ApiResponse<PipelineStage[]>> => {
    const response = await api.get(`${API_ROUTES.DEALS}/pipeline/${pipelineId}`);
    return response.data;
  },

  moveStage: async (id: string, stageId: string): Promise<ApiResponse<Deal>> => {
    const response = await api.patch(`${API_ROUTES.DEALS}/${id}/stage`, { stageId });
    return response.data;
  },

  markWon: async (id: string): Promise<ApiResponse<Deal>> => {
    const response = await api.patch(`${API_ROUTES.DEALS}/${id}/won`);
    return response.data;
  },

  markLost: async (id: string, lostReason?: string): Promise<ApiResponse<Deal>> => {
    const response = await api.patch(`${API_ROUTES.DEALS}/${id}/lost`, { lostReason });
    return response.data;
  },

  assign: async (id: string, assignedToId: string): Promise<ApiResponse<Deal>> => {
    const response = await api.patch(`${API_ROUTES.DEALS}/${id}/assign`, { assignedToId });
    return response.data;
  },

  addContact: async (id: string, data: { contactId: string; role?: string; isPrimary?: boolean }) => {
    await api.post(`${API_ROUTES.DEALS}/${id}/contacts`, data);
  },

  removeContact: async (id: string, contactId: string) => {
    await api.delete(`${API_ROUTES.DEALS}/${id}/contacts/${contactId}`);
  },

  bulkDelete: async (ids: string[]) => {
    await api.post(`${API_ROUTES.DEALS}/bulk-delete`, { ids });
  },

  getActivities: createSubResourceGetter<Activity>(API_ROUTES.DEALS, 'activities'),
  getNotes: createSubResourceGetter<Note>(API_ROUTES.DEALS, 'notes'),
  getTasks: createSubResourceGetter<Task>(API_ROUTES.DEALS, 'tasks'),
};
